<template>
  <div class="bulk-panel">
    <section
      class="upload-drop"
      :class="{ 'is-drag-over': isDragOver, 'has-files': queue.length > 0 }"
      @drop.prevent="onDrop"
      @dragover.prevent="isDragOver = true"
      @dragleave.prevent="isDragOver = false"
      @click="!isUploading && $refs.fileInput.click()"
      role="button"
      tabindex="0"
      @keydown.enter.prevent="!isUploading && $refs.fileInput.click()"
      @keydown.space.prevent="!isUploading && $refs.fileInput.click()"
    >
      <input
        ref="fileInput"
        type="file"
        multiple
        class="home-upload-input"
        :accept="acceptString"
        @change="onFileInput"
        @click.stop
      >
      <div class="upload-drop-inner">
        <i class="fas fa-cloud-upload-alt" aria-hidden="true"></i>
        <h2>Upload certificates</h2>
        <p class="upload-purpose">
          Drop COI files to collect for
          <strong>{{ insuredLabel }}</strong>
          against
          <strong>{{ checkName || 'this project’s insurance requirements' }}</strong>.
          The requesting team will review them before processing.
        </p>
        <p class="upload-hint">
          or click to browse · {{ acceptHumanString }} (max {{ maxFileMb }}MB each)
        </p>
      </div>
    </section>

    <div v-if="queue.length > 0" class="queue-list">
      <div class="queue-header">
        <div class="queue-header-left">
          <strong>{{ queue.length }}</strong>
          {{ queue.length === 1 ? 'file' : 'files' }} ·
          <span class="text-muted">{{ totalSizeHuman }} total</span>
        </div>
        <div class="queue-header-right" v-if="!isUploading">
          <button
            v-if="!hasAnySuccess"
            type="button"
            class="btn-ghost"
            @click="clearAll"
          >
            Clear
          </button>
          <button
            v-else
            type="button"
            class="btn-ghost"
            @click="clearCompletedAndAddMore"
          >
            Start a new batch
          </button>
        </div>
      </div>

      <div class="queue-items">
        <div
          v-for="item in queue"
          :key="item.id"
          class="queue-item"
          :class="{
            'is-error': item.status === 'error',
            'is-success': item.status === 'success',
            'is-uploading': item.status === 'uploading',
          }"
        >
          <div class="queue-item-icon">
            <i :class="iconForStatus(item.status)"></i>
          </div>
          <div class="queue-item-body">
            <div class="queue-item-name" :title="item.file.name">
              {{ item.file.name }}
            </div>
            <div class="queue-item-meta">
              <span>{{ formatBytes(item.file.size) }}</span>
              <span v-if="item.status === 'success'" class="status-ok">
                Uploaded · received
              </span>
              <span v-else-if="item.status === 'error'" class="status-err">
                {{ item.error || 'Upload failed' }}
              </span>
              <span v-else-if="item.status === 'uploading'" class="status-progress">
                Uploading {{ item.progress }}%
              </span>
              <span v-else class="status-queued">Queued</span>
            </div>
            <div v-if="item.status === 'uploading'" class="queue-item-progress">
              <div
                class="queue-item-progress-fill"
                :style="{ width: item.progress + '%' }"
              ></div>
            </div>
          </div>
          <div class="queue-item-action">
            <button
              v-if="item.status === 'queued' && !isUploading"
              type="button"
              class="icon-btn"
              @click.stop="removeItem(item.id)"
              aria-label="Remove file from queue"
            >
              <i class="fas fa-times"></i>
            </button>
            <button
              v-else-if="item.status === 'error'"
              type="button"
              class="icon-btn retry-btn"
              @click.stop="retryItem(item.id)"
              :disabled="isUploading"
              aria-label="Retry this file"
            >
              <i class="fas fa-redo"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="queue.length > 0" class="action-bar">
      <div class="action-bar-summary">
        <span v-if="!batchFinished">
          <strong>{{ queuedCount }}</strong> ready to upload
          <span v-if="successCount > 0">
            · <strong>{{ successCount }}</strong> already uploaded
          </span>
          <span v-if="errorCount > 0" class="text-warn">
            · <strong>{{ errorCount }}</strong> failed
          </span>
        </span>
        <span v-else class="batch-summary">
          Batch complete: <strong>{{ successCount }}</strong> uploaded
          <span v-if="errorCount > 0" class="text-warn">
            · <strong>{{ errorCount }}</strong> failed
          </span>
        </span>
      </div>
      <button
        type="button"
        class="btn-primary"
        :disabled="!canStart"
        @click="startBatch"
      >
        <i v-if="!isUploading" class="fas fa-cloud-upload-alt" aria-hidden="true"></i>
        <i v-else class="fas fa-spinner fa-spin" aria-hidden="true"></i>
        {{ uploadButtonLabel }}
      </button>
    </div>

    <section v-if="batchFinished && successCount > 0" class="purpose-panel purpose-panel--success">
      <div class="purpose-icon" aria-hidden="true">
        <i class="fas fa-check-circle"></i>
      </div>
      <div class="purpose-body">
        <h2>Documents received</h2>
        <p>
          Thanks — we’ve received your documents. The requesting team will process
          them and follow up with results. You can add more files above or close this page.
        </p>
      </div>
    </section>
  </div>
</template>

<script>
import api from '@/services/api';
import { UI_CONSTANTS } from '@/constants';

const MAX_CONCURRENCY = 3;

let _nextId = 0;
function newId() {
  _nextId += 1;
  return `f${_nextId}`;
}

export default {
  name: 'SecureUploadBulkPanel',
  props: {
    token: {
      type: String,
      required: true,
    },
    project: {
      type: Object,
      default: null,
    },
    checkName: {
      type: String,
      default: '',
    },
    insuredLabel: {
      type: String,
      default: 'your insured',
    },
  },
  data() {
    return {
      queue: [],
      isDragOver: false,
      isUploading: false,
      maxFileMb: 50,
      acceptString: UI_CONSTANTS.COI_UPLOAD_SECURE_ACCEPT,
      acceptHumanString: UI_CONSTANTS.COI_UPLOAD_SECURE_ACCEPT_LABEL,
    };
  },
  computed: {
    queuedCount() {
      return this.queue.filter((q) => q.status === 'queued').length;
    },
    successCount() {
      return this.queue.filter((q) => q.status === 'success').length;
    },
    errorCount() {
      return this.queue.filter((q) => q.status === 'error').length;
    },
    hasAnySuccess() {
      return this.successCount > 0;
    },
    totalSizeHuman() {
      const total = this.queue.reduce((acc, q) => acc + (q.file?.size || 0), 0);
      return this.formatBytes(total);
    },
    canStart() {
      return !this.isUploading && this.queuedCount > 0;
    },
    batchFinished() {
      return (
        !this.isUploading
        && this.queue.length > 0
        && this.queuedCount === 0
      );
    },
    uploadButtonLabel() {
      if (this.isUploading) {
        const done = this.successCount + this.errorCount;
        return `Uploading… (${done}/${this.queue.length})`;
      }
      if (this.queuedCount === 0) {
        return 'All files processed';
      }
      return `Upload ${this.queuedCount} file${this.queuedCount === 1 ? '' : 's'}`;
    },
  },
  methods: {
    onFileInput(e) {
      this.addFiles(Array.from(e.target.files || []));
      e.target.value = '';
    },
    onDrop(e) {
      this.isDragOver = false;
      const files = Array.from(e.dataTransfer?.files || []);
      this.addFiles(files);
    },
    addFiles(files) {
      const existingNames = new Set(
        this.queue.map((q) => `${q.file.name}::${q.file.size}`),
      );
      const accepted = [];
      const rejected = [];
      for (const f of files) {
        const key = `${f.name}::${f.size}`;
        if (existingNames.has(key)) continue;
        existingNames.add(key);

        const ext = (f.name.split('.').pop() || '').toLowerCase();
        if (!this.acceptString.split(',').some((a) => a.replace('.', '') === ext)) {
          rejected.push({ name: f.name, reason: 'unsupported file type' });
          continue;
        }
        if (f.size > this.maxFileMb * 1024 * 1024) {
          rejected.push({ name: f.name, reason: `larger than ${this.maxFileMb}MB` });
          continue;
        }
        accepted.push(f);
      }

      for (const f of accepted) {
        this.queue.push({
          id: newId(),
          file: f,
          status: 'queued',
          progress: 0,
          error: null,
          documentId: null,
        });
      }

      for (const r of rejected) {
        this.queue.push({
          id: newId(),
          file: { name: r.name, size: 0 },
          status: 'error',
          progress: 0,
          error: r.reason,
          documentId: null,
        });
      }
    },
    removeItem(id) {
      this.queue = this.queue.filter((q) => q.id !== id);
    },
    clearAll() {
      if (this.isUploading) return;
      this.queue = [];
    },
    clearCompletedAndAddMore() {
      this.queue = this.queue.filter((q) => q.status !== 'success');
      this.$refs.fileInput && this.$refs.fileInput.click();
    },
    retryItem(id) {
      const item = this.queue.find((q) => q.id === id);
      if (!item || item.status !== 'error') return;
      if (!item.file || !item.file.size) return;
      item.status = 'queued';
      item.progress = 0;
      item.error = null;
      this.startBatch();
    },
    async startBatch() {
      if (this.isUploading) return;
      const queued = this.queue.filter((q) => q.status === 'queued');
      if (queued.length === 0) return;

      this.isUploading = true;
      try {
        let cursor = 0;
        const next = () => {
          while (cursor < queued.length) {
            const item = queued[cursor];
            cursor += 1;
            if (item.status !== 'queued') continue;
            return this.uploadOne(item).then(next);
          }
          return Promise.resolve();
        };
        const workers = [];
        for (let i = 0; i < Math.min(MAX_CONCURRENCY, queued.length); i += 1) {
          workers.push(next());
        }
        await Promise.all(workers);
      } finally {
        this.isUploading = false;
      }
    },
    async uploadOne(item) {
      item.status = 'uploading';
      item.progress = 0;
      item.error = null;

      try {
        const formData = new FormData();
        formData.append('file', item.file);

        const response = await api.post(
          `/secure-upload/${this.token}/`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (evt) => {
              if (evt.total) {
                item.progress = Math.round((evt.loaded * 100) / evt.total);
              }
            },
          },
        );

        if (response.data?.status === 'success') {
          item.status = 'success';
          item.progress = 100;
          item.documentId = response.data?.document?.id || null;
        } else {
          item.status = 'error';
          item.error = response.data?.message || 'Upload failed';
        }
      } catch (err) {
        item.status = 'error';
        item.error =
          err.response?.data?.message
          || err.message
          || 'Upload failed. Check your connection and retry.';
      }
    },

    iconForStatus(status) {
      switch (status) {
        case 'success': return 'fas fa-check-circle';
        case 'error': return 'fas fa-exclamation-circle';
        case 'uploading': return 'fas fa-spinner fa-spin';
        default: return 'fas fa-file';
      }
    },
    formatBytes(bytes) {
      if (!bytes && bytes !== 0) return '';
      if (bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
      return `${(bytes / Math.pow(k, i)).toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
    },
  },
};
</script>

<style scoped>
.bulk-panel {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.upload-drop {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 2px dashed #cbd5e1;
  border-radius: 0.75rem;
  background: #fff;
  padding: 1.5rem 1rem;
  transition: border-color 0.15s ease, background 0.15s ease;
  user-select: none;
}

.upload-drop:hover,
.upload-drop.is-drag-over {
  border-color: var(--p-accent, #1e3a5f);
  background: #f8fafc;
}

.upload-drop:focus-visible {
  outline: 2px solid var(--p-accent, #1e3a5f);
  outline-offset: 2px;
}

.upload-drop.has-files {
  padding: 1.1rem 1rem;
}

.home-upload-input {
  display: none;
}

.upload-drop-inner {
  text-align: center;
  max-width: 24rem;
}

.upload-drop-inner i {
  font-size: 1.75rem;
  color: var(--p-accent, #1e3a5f);
  margin-bottom: 0.55rem;
  display: block;
}

.upload-drop-inner h2 {
  margin: 0 0 0.4rem;
  font-size: 1.15rem;
  font-weight: 700;
  color: #0f172a;
}

.upload-purpose {
  margin: 0 0 0.55rem;
  font-size: 0.9rem;
  color: #475569;
  line-height: 1.4;
}

.upload-purpose strong {
  color: #0f172a;
  font-weight: 600;
}

.upload-hint {
  margin: 0;
  font-size: 0.82rem;
  color: #64748b;
}

.queue-list {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 0.85rem 1rem;
}

.queue-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  color: #475569;
}

.text-muted {
  color: #94a3b8;
}

.text-warn {
  color: #b45309;
}

.queue-items {
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  overflow: hidden;
  max-height: 360px;
  overflow-y: auto;
}

.queue-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.7rem 0.85rem;
  border-bottom: 1px solid #f1f5f9;
  background: #fff;
}

.queue-item:last-child {
  border-bottom: none;
}

.queue-item.is-success {
  background: #f8fafc;
}

.queue-item.is-error {
  background: #fff7ed;
}

.queue-item-icon {
  width: 1.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  flex-shrink: 0;
}

.queue-item.is-success .queue-item-icon { color: #047857; }
.queue-item.is-error .queue-item-icon { color: #b45309; }
.queue-item.is-uploading .queue-item-icon { color: var(--p-accent, #1e3a5f); }

.queue-item-body {
  flex: 1;
  min-width: 0;
}

.queue-item-name {
  font-weight: 600;
  color: #0f172a;
  font-size: 0.88rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.queue-item-meta {
  display: flex;
  gap: 0.65rem;
  flex-wrap: wrap;
  font-size: 0.8rem;
  color: #64748b;
  margin-top: 0.1rem;
}

.status-ok { color: #047857; font-weight: 600; }
.status-err { color: #b45309; font-weight: 600; }
.status-progress { color: var(--p-accent, #1e3a5f); font-weight: 600; }

.queue-item-progress {
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  margin-top: 0.35rem;
  overflow: hidden;
}

.queue-item-progress-fill {
  height: 100%;
  background: var(--p-accent, #1e3a5f);
  transition: width 0.2s ease;
}

.icon-btn {
  background: transparent;
  border: 1px solid #e2e8f0;
  color: #475569;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.icon-btn:hover {
  border-color: #94a3b8;
}

.icon-btn.retry-btn {
  border-color: #fcd34d;
  color: #b45309;
}

.icon-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.action-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.action-bar-summary {
  font-size: 0.9rem;
  color: #475569;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  padding: 0.65rem 1rem;
  background: var(--p-accent, #1e3a5f);
  color: #fff;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
}

.btn-primary:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.btn-ghost {
  background: none;
  border: 1px solid #cbd5e1;
  color: #475569;
  padding: 0.35rem 0.65rem;
  border-radius: 0.375rem;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
}

.purpose-panel {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.85rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1rem 1.1rem;
}

.purpose-panel--success .purpose-icon {
  background: #ecfdf5;
  color: #047857;
}

.purpose-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.55rem;
  background: #f1f5f9;
  color: #334155;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
}

.purpose-body h2 {
  margin: 0 0 0.35rem;
  font-size: 1.05rem;
  font-weight: 700;
}

.purpose-body p {
  margin: 0;
  font-size: 0.9rem;
  color: #475569;
  line-height: 1.45;
}

@media (max-width: 640px) {
  .action-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .btn-primary {
    width: 100%;
  }
}
</style>
